import { useZohoSession } from '../contexts/ZohoSessionContext';
import { Button } from './ui/button';
import { X, ExternalLink, ShieldCheck, AlertTriangle, Loader2 } from 'lucide-react';
import { useState, useRef, useEffect } from 'react';

const ZOHO_CRM_URL = 'https://crm.zoho.eu/crm/encon/tab/Home';

// Detect iPad / iPhone / iPod, including iPadOS 13+ which masquerades as Mac
// with touch support. On these devices, popup.closed polling is unreliable
// because Safari aggressively suspends background tabs, so we show a manual
// "I've signed in" fallback button after a short delay.
const isIOSDevice = () => {
  if (typeof navigator === 'undefined') return false;
  const ua = navigator.userAgent || '';
  if (/iPad|iPhone|iPod/.test(ua)) return true;
  // iPadOS 13+ reports as MacIntel but has touch points
  if (ua.includes('Mac') && typeof document !== 'undefined' && navigator.maxTouchPoints > 1) return true;
  return false;
};

/**
 * Gate modal shown whenever a CRM feature is attempted while the user hasn't
 * confirmed they are signed into Zoho CRM in this browser.
 *
 * Flow:
 *   1. User clicks "Open Zoho CRM & Sign in" → popup opens to crm.zoho.eu.
 *      Zoho either renders the CRM home (already signed in) or its own login
 *      page (not yet signed in).
 *   2. We poll `popup.closed` every 500 ms. When the user closes the popup
 *      (signalling they are done with Zoho), we auto-unlock features for
 *      this browser session.
 *   3. iPad/iOS Safari aggressively suspends background tabs, so the
 *      popup.closed signal is unreliable on those devices. For iOS only,
 *      after 3s we expose a manual "I've signed in — continue" button.
 *      Desktop users keep the auto-detect-only flow.
 *
 * If the user isn't linked to any Zoho seat at all we show a different
 * message pointing them at their admin.
 */
const ZohoLoginGateModal = () => {
  const { promptOpen, closePrompt, confirm, isLinked, isSuperAdmin, loading } = useZohoSession();
  const [popup, setPopup] = useState(null);
  const [iosWaiting, setIosWaiting] = useState(false);
  const pollRef = useRef(null);
  const isIOS = isIOSDevice();

  // Treat super admins as always linked — they need to be able to
  // diagnose CRM issues even when the directory lookup transiently fails
  // or when the deployed backend hasn't picked up a fresh refresh token.
  const effectivelyLinked = isLinked || isSuperAdmin;

  // iOS path: skip popup polling entirely (iPad Safari doesn't reliably
  // report tab closure). Open Zoho in a new tab and surface the manual
  // "I've signed in — continue" button right away.
  const handleOpenZohoIOS = () => {
    window.open(ZOHO_CRM_URL, '_blank', 'noopener,noreferrer');
    setIosWaiting(true);
  };

  // Desktop path: open a tracked popup and poll popup.closed.
  const handleOpenZohoDesktop = () => {
    const p = window.open(ZOHO_CRM_URL, 'zoho_session_check',
      'width=560,height=680,left=120,top=80,noopener=no');
    if (!p) {
      // Popup blocked — fall back to opening in a new tab and treat as
      // the iOS flow so the user gets a manual confirm button.
      window.open(ZOHO_CRM_URL, '_blank', 'noopener,noreferrer');
      setIosWaiting(true);
      return;
    }
    setPopup(p);
    pollRef.current = setInterval(() => {
      if (p.closed) {
        clearInterval(pollRef.current);
        pollRef.current = null;
        setPopup(null);
        confirm();
      }
    }, 500);
  };

  const handleOpenZoho = () => {
    if (isIOS) handleOpenZohoIOS();
    else handleOpenZohoDesktop();
  };

  // Manual confirm — used on iOS and as the popup-blocked fallback.
  const handleManualConfirm = () => {
    if (pollRef.current) {
      clearInterval(pollRef.current);
      pollRef.current = null;
    }
    if (popup && !popup.closed) {
      try { popup.close(); } catch (e) { /* ignore */ }
    }
    setPopup(null);
    setIosWaiting(false);
    confirm();
  };

  // Cleanup poller if the modal is dismissed mid-flow.
  useEffect(() => {
    return () => {
      if (pollRef.current) clearInterval(pollRef.current);
      if (popup && !popup.closed) popup.close();
    };
  }, [popup]);

  if (!promptOpen) return null;

  return (
    <div
      className="fixed inset-0 z-[200] flex items-center justify-center bg-black/50 p-4"
      onClick={popup ? undefined : closePrompt}
      data-testid="zoho-login-gate-modal"
    >
      <div
        className="bg-white rounded-2xl shadow-2xl max-w-md w-full overflow-hidden border-2 border-orange-200"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start gap-3 p-5 border-b border-gray-100">
          <div className="w-11 h-11 rounded-xl bg-orange-100 flex items-center justify-center shrink-0">
            <ShieldCheck className="w-6 h-6 text-orange-600" />
          </div>
          <div className="flex-1 min-w-0">
            <h3 className="font-extrabold text-gray-900 text-lg leading-tight">
              {!effectivelyLinked && !loading
                ? 'No Zoho CRM access'
                : iosWaiting
                  ? 'Sign in, then come back here'
                  : popup
                    ? 'Waiting for Zoho sign-in…'
                    : 'Sign in to Zoho CRM first'}
            </h3>
            <p className="text-xs text-gray-500 mt-1">
              {!effectivelyLinked && !loading
                ? "Your account isn't linked to a Zoho CRM seat yet."
                : iosWaiting
                  ? "We've opened Zoho in a new tab. Sign in there, then return here and tap Continue."
                  : popup
                    ? "When you're done, close the Zoho popup to continue."
                    : "We'll open Zoho in a popup. Sign in there, close the popup when done, and CRM features unlock."}
            </p>
          </div>
          {!popup && !iosWaiting && (
            <button
              onClick={closePrompt}
              className="text-gray-400 hover:text-gray-600 shrink-0"
              data-testid="zoho-login-gate-close"
              aria-label="Close"
            >
              <X className="w-5 h-5" />
            </button>
          )}
        </div>

        <div className="p-5 space-y-4">
          {!effectivelyLinked && !loading ? (
            <div className="rounded-xl bg-amber-50 border border-amber-200 p-3 text-sm text-amber-800 flex items-start gap-2">
              <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" />
              <div>
                Ask a C30 admin to add you to Zoho CRM (Settings → Users &amp; Control → Users). Once your email is on their roster, CRM features will unlock here automatically.
              </div>
            </div>
          ) : iosWaiting ? (
            <div className="flex flex-col items-center justify-center py-2 gap-4">
              <div className="text-sm text-gray-700 text-center max-w-xs leading-relaxed">
                Once you&apos;ve finished signing into Zoho in the other tab, come back to this tab and tap below.
              </div>
              <Button
                onClick={handleManualConfirm}
                className="w-full rounded-xl h-11 text-white font-semibold shadow-md"
                style={{ background: 'linear-gradient(135deg, #ee7203 0%, #ff8d2a 100%)' }}
                data-testid="zoho-login-gate-manual-confirm"
              >
                I&apos;ve signed in — continue
              </Button>
              <div className="flex items-center justify-between w-full text-[11px]">
                <button
                  onClick={() => window.open(ZOHO_CRM_URL, '_blank', 'noopener,noreferrer')}
                  className="text-gray-500 hover:text-gray-700 underline cursor-pointer"
                  data-testid="zoho-login-gate-reopen-zoho"
                >
                  Re-open Zoho tab
                </button>
                <button
                  onClick={() => { setIosWaiting(false); }}
                  className="text-gray-500 hover:text-gray-700 underline cursor-pointer"
                  data-testid="zoho-login-gate-cancel-ios"
                >
                  Cancel
                </button>
              </div>
            </div>
          ) : popup ? (
            <div className="flex flex-col items-center justify-center py-4 gap-3">
              <Loader2 className="w-8 h-8 text-orange-500 animate-spin" />
              <div className="text-sm text-gray-700 text-center max-w-xs">
                Complete your sign-in in the Zoho popup, then close it. CRM features will unlock automatically.
              </div>
              <Button
                onClick={handleManualConfirm}
                className="w-full rounded-xl h-11 text-white font-semibold shadow-md mt-2"
                style={{ background: 'linear-gradient(135deg, #ee7203 0%, #ff8d2a 100%)' }}
                data-testid="zoho-login-gate-desktop-manual-confirm"
              >
                I&apos;ve signed in — continue
              </Button>
              <button
                onClick={() => { try { popup.close(); } catch(e) {} setPopup(null); if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; } }}
                className="text-xs text-gray-500 hover:text-gray-700 underline mt-1 cursor-pointer"
                data-testid="zoho-login-gate-cancel-popup"
              >
                Cancel and close popup
              </button>
            </div>
          ) : (
            <>
              <Button
                onClick={handleOpenZoho}
                className="w-full rounded-xl h-11 text-white font-semibold shadow-md hover:shadow-lg"
                style={{ background: 'linear-gradient(135deg, #ee7203 0%, #ff8d2a 100%)' }}
                data-testid="zoho-login-gate-open-crm"
              >
                <ExternalLink className="w-4 h-4 mr-2" /> Open Zoho CRM &amp; Sign in
              </Button>

              <p className="text-[11px] text-gray-500 text-center leading-relaxed">
                {isIOS
                  ? "We'll open Zoho in a new tab. Sign in there, come back to this tab, then tap Continue."
                  : "Already signed into Zoho in another tab? Zoho will recognise your session automatically — just close the popup when it shows CRM."}
              </p>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default ZohoLoginGateModal;
